import React from 'react';
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmDialog from '../parts/ConfirmDialog';
import AlertDialog from '../parts/AlertDialog';

const styles = theme => ({
  behavioursWrapper: {
    margin: '20px 0 40px',
    display: 'flex',
    alignItems: 'start',
    flexWrap: 'wrap',
  },
  behaviourWrapper: {
    textAlign: 'center',
    cursor: 'pointer',
    margin: 5,
    color: '#46606d',
    width: 100,
    '& img': {
      width: '100%',
      maxWidth: 42,
      padding: 8,
      border: '1px solid #ccc',
      borderRadius: 3,
      boxShadow: '0px 0px 20px rgba(128,128,128,0.3)',
    },
    '&:hover img': {
      maxWidth: 58,
      padding: 0,
    },
  },
  behaviourWrapperDisabled: {
    opacity: 0.5,
  },
  behaviourLabel: {
    fontSize: 13,
  },
  saveButton: {
    display: 'block',
    margin: '10px auto',
    padding: '12px 24px',
    backgroundColor: '#f56a12',
    '&:hover': {
      backgroundColor: '#C24C03',
    },
  },
});

const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
  };
};

class ParentBehaviourPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      meta: [],
      data: [],
      isLoading: false,
      isLoaded: false,
      isConfirmDialogOpen: false,
      isAlertDialogOpen: false,
      selectedBehaviourId: null,
    };

    this.axiosCancel = null;

    this.onBehaviourClick = this.onBehaviourClick.bind(this);
    this.onConfirmClick = this.onConfirmClick.bind(this);
  }

  componentDidMount() {
    this.reloadData();
  }

  componentWillUnmount() {
    if (this.axiosCancel) {
      this.axiosCancel();
    }
  }

  onBehaviourClick(behaviour) {
    if (behaviour.is_daily_limit_reached) {
      this.setState({
        isAlertDialogOpen: true,
      });

      return;
    }

    this.setState({
      selectedBehaviourId: behaviour.id,
      isConfirmDialogOpen: true,
    });
  }

  onConfirmClick() {
    const {
      enqueueSnackbar,
      t,
      selectedPupil,
    } = this.props;
    const {
      selectedBehaviourId,
    } = this.state;

    axios
      .post(
        `${window.apiUrl}/addparentbehaviour/${selectedPupil.id}`,
        qs.stringify({
          behaviour_id: selectedBehaviourId,
        }),
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            selectedBehaviourId: null,
          });

          enqueueSnackbar(t('You have successfully awarded the behaviour'), {
            variant: 'success',
            autoHideDuration: 2000,
          });

          this.reloadData();
        } else {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        }

        this.setState({
          isConfirmDialogOpen: false,
        });
      })
      .catch((thrown) => {
        if (
          !thrown.response &&
          !axios.isCancel(thrown)
        ) {
          this.setState({
            isConfirmDialogOpen: false,
          });

          enqueueSnackbar(t('Network error detected. Please make sure you are connected to the internet.'), {
            variant: 'error',
            autoHideDuration: 2000,
          });
        }
      })
  }

  reloadData() {
    const {
      selectedPupil,
    } = this.props;

    if (!selectedPupil) {
      return;
    }

    this.setState({
      isLoading: true,
      isLoaded: false,
    });

    axios
      .get(
        `${window.apiUrl}/parentbehaviours/${selectedPupil.id}`,
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            meta: response.data.meta,
            data: response.data.data,
          });
        }

        this.setState({
          isLoading: false,
          isLoaded: true,
        });
      });
  }

  render() {
    const {
      classes,
      t,
      selectedPupil,
    } = this.props;
    const {
      data,
      isLoading,
      isLoaded,
      isConfirmDialogOpen,
      isAlertDialogOpen,
    } = this.state;

    const headingText = t('What behaviour do you want award to {pupil_name}?')
        .replace('{pupil_name}', selectedPupil.first_name);

    return (
      <div className="parent-behaviour-page">
        {isLoading && !isLoaded && (
          <CircularProgress />
        )}
        {!isLoading && isLoaded && (
          <>
            <Typography
              className={classes.sectionHeading}
              variant="h5"
              component="h5"
            >
              {headingText}
            </Typography>
            <div className={classes.behavioursWrapper}>
              {data.map((behaviour) => {
                let behaviourWrapper = classes.behaviourWrapper;
                if (behaviour.is_daily_limit_reached) {
                  behaviourWrapper = `${classes.behaviourWrapper} ${classes.behaviourWrapperDisabled}`;
                }

                return (
                  <div
                    key={behaviour.id}
                    className={behaviourWrapper}
                    onClick={this.onBehaviourClick.bind(this, behaviour)}
                  >
                    <img src={behaviour.icon_url} />
                    <Typography
                      variant="h6"
                      component="h6"
                      className={classes.behaviourLabel}
                    >
                      {behaviour.name}
                    </Typography>
                  </div>
                )
              })}
            </div>
          </>
        )}
        <ConfirmDialog
          useSuspense={false}
          title={t('Confirmation')}
          isOpen={isConfirmDialogOpen}
          onClose={() => this.setState({
            isConfirmDialogOpen: false,
          })}
          onConfirm={this.onConfirmClick}
        >
          {t('Are you sure you want to award this behaviour?')}
        </ConfirmDialog>
        <AlertDialog
          useSuspense={false}
          title={t('Daily limit reached')}
          isOpen={isAlertDialogOpen}
          onClose={() => this.setState({
            isAlertDialogOpen: false,
          })}
        >
          {t('You have reached the daily limit awards for this behaviour.')}
        </AlertDialog>
      </div>
    );
  }
}

export default connect(mapStateToProps)(
  withStyles(styles)(withSnackbar(withTranslation()(ParentBehaviourPage)))
);
